import React from "react";
import styled from "styled-components";
import { IBrand } from "../../../../Models/IBrand";
import {
    IRetailMatchChartData,
    IStoreScoreRecord,
} from "../../../../Models/IRetailMatch";
import { brandOverviewRecord } from "../../../../Util/RetailMatch/RetailMatchUtil";
import { GoogleMapsMarker, ReportH4 } from "../../../Common";
import { brandStores } from "../Util/RetailMatchUtil";
import { BrandLogoSmall } from "./BrandLogo/BrandLogoSmall";
import { IndexValuesDescription } from "./IndexValuesDescription";

interface LocationsAnalyzedSharingProps {
  brand: IBrand;
  benchmarkRecord: IStoreScoreRecord;
  chartData: IRetailMatchChartData;
}

const StyledTable = styled.table`
  table-layout: fixed;
  width: 100%;

  tr {
    font-size: 12px;
    border: solid 1px #e4e4e4;

    &.header {
      height: 46px;
      background-color: #f5f5f5;
    }

    th {
      padding: 0 10px 0 10px;
      vertical-align: middle;
      font-weight: normal;
    }

    td {
      padding: 5px 0 5px 10px;
      vertical-align: middle;

      &.index-value {
        color: white;
        border: solid 1px #e4e4e4;
        text-align: center;
      }

      &.address {
        max-width: 100px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }
`;

const TableTitleSpan = styled.span`
  margin-left: 6px;
  font-size: 15px;
`;

const TableTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div {
    color: #6d7278;
  }
`;

const getIndexBackgroundColor = (index: number) => {
    if (index < 49) {
        return "#e02020";
    }

    if (index > 49 && index <= 100) {
        return "#1ce180";
    }

    return "#2b8b49";
};

const summation = (a, b, c) => {
    return a + b + c;
};

const getHighestSegmentOfScores = (
    scores: IStoreScoreRecord[],
    segmentLength: number
) => {
    const highest = [];

    scores.forEach((score, index) => {
        if (index <= segmentLength - 1) {
            highest.push(score);
        }
    });

    return highest;
};

const getMiddleSegmentOfScores = (
    scores: IStoreScoreRecord[],
    segmentLength: number
) => {
    const startingIndex = Math.round(scores.length / 2) - 1;
    const middle = [];
    let count = 0;

    for (let i = startingIndex; count < segmentLength; i++) {
        middle.push(scores[i]);
        count += 1;
    }

    return middle;
};

const getLowestSegmentOfScores = (
    scores: IStoreScoreRecord[],
    segmentLength: number
) => {
    let count = 0;
    const lowest = [];

    for (let i = scores.length - 1; count < segmentLength; i--) {
        lowest.push(scores[i]);
        count += 1;
    }

    return lowest;
};

const getSegmentLength = (
    scores: IStoreScoreRecord[],
    numberOfSegments: number
) => {
    // Ideally we'd like at least 5 stores in a segment as per the design
    let segmentLength = 5;

    for (let i = segmentLength; i <= segmentLength && i >= 1; i -= 1) {
        const product = i * numberOfSegments;

        // Ensure the product of the segment length and number of segments does
        // not exceed the total amount of scores
        if (product <= scores.length) {
            segmentLength = i;
            break;
        }
    }

    return segmentLength;
};

const segmentStoreScores = (scores: IStoreScoreRecord[]) => {
    // Check if there are no stores. If not,
    // return here
    if (scores.length === 0) {
        return {
            highest: [],
            middle: [],
            lowest: [],
        };
    }

    // Check if there's only one store to compare against. If so,
    // return this store as the highest segment
    if (scores.length === 1) {
        return {
            highest: [scores[0]],
            middle: [],
            lowest: [],
        };
    }

    // Determine the number of segments
    const numberOfSegments = scores.length >= 3 ? 3 : 2;

    // Determine segment length
    const segmentLength = getSegmentLength(scores, numberOfSegments);

    // Get the top 5 locations from the beginning of the array
    const highest = getHighestSegmentOfScores(scores, segmentLength);

    // Get the lowest 5 locations from the tail of the array
    const lowest = getLowestSegmentOfScores(scores, segmentLength);

    // Get middle locations based on the number of segments. If there's only 2,
    // set it to an empty array as we'll only have highest and lowest segments
    const middle
    = numberOfSegments === 3
        ? getMiddleSegmentOfScores(scores, segmentLength)
        : [];

    return {
        highest,
        middle,
        lowest,
    };
};

const sortStoreScoresBySummationOfIndexValues = (
    scores: IStoreScoreRecord[]
) => {
    return scores.sort((a, b) => {
        return (
            summation(b.ccCntIdx, b.ccPerHHCntIdx, b.ccCorrIdx)
      - summation(a.ccCntIdx, a.ccPerHHCntIdx, a.ccCorrIdx)
        );
    });
};

const sortStoreScoresByIdealCustomerCount = (scores: IStoreScoreRecord[]) => {
    return scores.sort((a, b) => {
        return b.ccCntIdx - a.ccCntIdx;
    });
};

const renderTable = (hasHeader = true, stores: IStoreScoreRecord[]) => {
    return (
        <StyledTable>
            <thead>
                <tr
                    className="header"
                    style={{ visibility: hasHeader ? "visible" : "collapse" }}
                >
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Zip</th>
                    <th>Ideal Customer Count</th>
                    <th>Ideal Customer Ratio</th>
                    <th>Household Similarity</th>
                    <th>Mobile Device Volume</th>
                </tr>
            </thead>
            <tbody>
                {stores
                    .filter((store) => store)
                    .map((store, index) => {
                        return (
                            <tr key={index}>
                                <td className="address">{store.addr}</td>
                                <td>{store.city}</td>
                                <td>{store.st}</td>
                                <td style={{ minWidth: 68 }}>{store.zip}</td>
                                <td
                                    className="index-value"
                                    style={{
                                        backgroundColor: getIndexBackgroundColor(store.ccCntIdx),
                                        color: "white",
                                        border: "solid 1px #e4e4e4",
                                    }}
                                >
                                    {store.ccCntIdx}
                                </td>
                                <td
                                    className="index-value"
                                    style={{
                                        backgroundColor: getIndexBackgroundColor(
                                            store.ccPerHHCntIdx
                                        ),
                                        color: "white",
                                        border: "solid 1px #e4e4e4",
                                    }}
                                >
                                    {store.ccPerHHCntIdx}
                                </td>
                                <td
                                    className="index-value"
                                    style={{
                                        backgroundColor: getIndexBackgroundColor(store.ccCorrIdx),
                                        color: "white",
                                        border: "solid 1px #e4e4e4",
                                    }}
                                >
                                    {store.ccCorrIdx}
                                </td>
                                <td>{store.vCntIdx || "N/A"}</td>
                            </tr>
                        );
                    })}
            </tbody>
        </StyledTable>
    );
};

const targetSiteTitle = (
    <TableTitleWrapper>
        <ReportH4>
            <GoogleMapsMarker styles={{ width: 9, height: 16 }} />
            <TableTitleSpan>Your Target Site</TableTitleSpan>
        </ReportH4>

        <div style={{ display: "flex" }}>
            <span style={{ marginRight: 20 }}>Index Values:</span>
            <IndexValuesDescription color="#e02020" range="0-49" description="Low" />
            <IndexValuesDescription
                color="#1ce180"
                range="50-100"
                description="Average"
                styles={{ marginLeft: 11 }}
            />
            <IndexValuesDescription
                color="#2b8b49"
                range="101-200"
                description="High"
                styles={{ marginLeft: 11 }}
            />
        </div>
    </TableTitleWrapper>
);

export const LocationsAnalyzedSharing = ({
    brand,
    benchmarkRecord,
    chartData,
}: LocationsAnalyzedSharingProps) => {
    const brandName = brand.brandName;
    const brandLogo = brand.logoPath;

    const storesForBrand = brandStores(chartData.storeScores, brand.brandId);

    const benchmarkScores = sortStoreScoresByIdealCustomerCount(storesForBrand);
    const benchmarkSegments = segmentStoreScores(benchmarkScores);

    const overviewRecord = brandOverviewRecord(brand.brandId, chartData.overview);

    const benchmarkSetTitle = (title: string, hasLogo = false) => {
        return (
            <TableTitleWrapper>
                {hasLogo && (
                    <ReportH4>
                        <BrandLogoSmall brandLogo={brandLogo} brandName={brandName} />
                        <TableTitleSpan>{brandName} Benchmark Set</TableTitleSpan>
                    </ReportH4>
                )}

                <div
                    style={{ display: "flex", justifyContent: "flex-end", flexGrow: 1 }}
                >
                    <ReportH4>
                        <TableTitleSpan style={{ justifyContent: "flex-end" }}>
                            {title} Locations
                        </TableTitleSpan>
                    </ReportH4>
                </div>
            </TableTitleWrapper>
        );
    };

    return (
        <>
            <div style={{ marginBottom: 21 }}>
                <h3 style={{ marginLeft: 0, paddingLeft: 0 }}>
          Which locations were selected for comparison?
                </h3>
                <p style={{ marginBottom: 0 }}>
                    {overviewRecord.sampleCount || 0} similar {brandName} locations were
          selected to compare {benchmarkRecord.addr} based on urban density and
          proximity.
                </p>
            </div>

            {/* Target Site */}
            <div style={{ marginBottom: 10 }}>
                {targetSiteTitle}
                {renderTable(true, [benchmarkRecord])}
            </div>

            {/* Top Locations */}
            {benchmarkSegments.highest && benchmarkSegments.highest.length > 0 && (
                <div style={{ marginBottom: 5 }}>
                    {benchmarkSetTitle(`Top ${benchmarkSegments.highest.length}`, true)}
                    {renderTable(false, benchmarkSegments.highest)}
                </div>
            )}

            {/* Middle Locations */}
            {benchmarkSegments.middle && benchmarkSegments.middle.length > 0 && (
                <div style={{ marginBottom: 5 }}>
                    {benchmarkSetTitle(`Middle ${benchmarkSegments.middle.length}`)}
                    {renderTable(false, benchmarkSegments.middle)}
                </div>
            )}

            {/* Lowest Locations */}
            {benchmarkSegments.lowest && benchmarkSegments.lowest.length > 0 && (
                <div>
                    {benchmarkSetTitle(`Bottom ${benchmarkSegments.lowest.length}`)}
                    {renderTable(false, benchmarkSegments.lowest)}
                </div>
            )}
        </>
    );
};
